import React from "react";

// Which live providers power the map. `providers` is the scan's provider map
// ({ tomtom:{enabled,reason}, ticketmaster:{enabled,reason} }). `googleTraffic`
// is true on the Google map where the live TrafficLayer colouring is shown.
function SourceRow({ label, live }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-slate-500">{label}</span>
      <span className={`inline-flex items-center gap-1 font-semibold ${live ? "text-emerald-600" : "text-slate-400"}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${live ? "bg-emerald-500" : "bg-slate-300"}`} />
        {live ? "Live" : "Unavailable"}
      </span>
    </div>
  );
}

// Bottom-left map overlay: live incident counters, "Show all nearby" toggle and
// per-source live/unavailable status. Counters reflect the FULL available set;
// `hidden` is how many the 12-marker cap dropped from the default view.
export default function MapLegend({ counts, hidden = 0, showAll = false, onToggle, providers, googleTraffic = false }) {
  const c = counts || { affecting: 0, nearby: 0, events: 0 };
  const p = providers || {};
  const hereLive = p.here ? !!p.here.enabled : false;
  const tmLive = p.ticketmaster ? !!p.ticketmaster.enabled : false;
  const wxLive = p.weather ? !!p.weather.enabled : false;
  const hasProviders = !!(p.here || p.ticketmaster || p.weather);
  const trafficNote = hasProviders && !hereLive && (tmLive || googleTraffic);

  return (
    <div
      className="absolute bottom-3 left-3 z-[500] rounded-xl bg-white/95 backdrop-blur border border-slate-200 shadow-md px-3 py-2 text-[12px] leading-5 text-slate-700 select-none max-w-[230px]"
      data-testid="map-legend"
    >
      <div className="flex items-center justify-between gap-2" data-testid="legend-affecting">
        <span>🔴 Affecting route</span><b className="tabular-nums">{c.affecting}</b>
      </div>
      <div className="flex items-center justify-between gap-2" data-testid="legend-nearby">
        <span>⚠️ Nearby route</span><b className="tabular-nums">{c.nearby}</b>
      </div>
      <div className="flex items-center justify-between gap-2" data-testid="legend-events">
        <span>🎫 Events affecting run</span><b className="tabular-nums">{c.events}</b>
      </div>
      {!showAll && hidden > 0 && (
        <div className="mt-0.5 text-[11px] text-slate-400" data-testid="legend-hidden">
          +{hidden} more nearby
        </div>
      )}

      {onToggle && (
        <button
          type="button"
          onClick={() => onToggle(!showAll)}
          className="mt-1.5 pt-1.5 border-t border-slate-100 w-full flex items-center justify-between gap-3 font-semibold text-slate-600 hover:text-indigo-600 transition-colors"
          data-testid="legend-show-all-toggle"
        >
          <span>👁 Show all nearby</span>
          <span className={showAll ? "text-indigo-600" : "text-slate-400"}>{showAll ? "ON" : "OFF"}</span>
        </button>
      )}

      {(hasProviders || googleTraffic) && (
        <div className="mt-1.5 pt-1.5 border-t border-slate-100 space-y-0.5" data-testid="legend-sources">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Live sources</div>
          {p.here !== undefined && <SourceRow label="🚦 Traffic incidents" live={hereLive} />}
          {p.ticketmaster !== undefined && <SourceRow label="🎫 Events" live={tmLive} />}
          {p.weather !== undefined && <SourceRow label="🌦 Weather" live={wxLive} />}
          {googleTraffic && <SourceRow label="🟢 Google traffic" live />}
        </div>
      )}

      {trafficNote && (
        <div className="mt-1 text-[10px] text-slate-400 leading-tight" data-testid="legend-traffic-note">
          Traffic incidents unavailable · Events and live traffic still active
        </div>
      )}
    </div>
  );
}
